import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { theme } from "../../theme/colors";
import { canLogPostAction } from "../../utils/accountTypes";
import { getPostActions, logPostAction, removePostAction } from "../../utils/feedApi";
import { formatActionTypeLabel } from "../../utils/actionTypeOptions";
import {
  computeToggledActionCounts,
  getActionSummaryAccessibilityLabel,
  getActionSummaryLabel,
  getActionUserDisplayName,
  getActionUserInitials,
  getDisplayActionCount,
  getToggleFallbackFromErrorCode,
  resolveToggleErrorCode,
  shouldShowActionRow,
  shouldShowActionToggle,
} from "../../utils/actionAggregationLogic";

export default function ActionAggregationCard({
  post,
  isAuthor = false,
  isVerified = false,
  verifiedCountsOnly = false,
  currentUserId = null,
  onAuthRequired,
  onActionCountChange,
  onError,
}) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasLogged, setHasLogged] = useState(!!post.viewer_has_logged_action);
  const [actionCount, setActionCount] = useState(post.action_count || 0);
  const [verifiedActionCount, setVerifiedActionCount] = useState(
    post.verified_action_count ?? 0
  );

  useEffect(() => {
    setHasLogged(!!post.viewer_has_logged_action);
    setActionCount(post.action_count || 0);
    setVerifiedActionCount(post.verified_action_count ?? 0);
  }, [post.id, post.viewer_has_logged_action, post.action_count, post.verified_action_count]);

  const displayActionCount = getDisplayActionCount({
    verifiedCountsOnly,
    actionCount,
    verifiedActionCount,
  });

  const loadActions = async () => {
    setLoading(true);
    try {
      const response = await getPostActions(post.id);
      setActions(response.results || []);
    } catch (error) {
      console.error("Error loading actions:", error);
      onError?.("Couldn't load actions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const applyToggle = (logged, serverCount, serverVerifiedCount, fallbackDelta) => {
    const next = computeToggledActionCounts({
      logged,
      serverCount,
      serverVerifiedCount,
      fallbackDelta,
      prevActionCount: actionCount,
      prevVerifiedActionCount: verifiedActionCount,
      isVerified,
    });
    setHasLogged(next.logged);
    setActionCount(next.actionCount);
    setVerifiedActionCount(next.verifiedActionCount);
    onActionCountChange?.({
      logged: next.logged,
      actionCount: serverCount,
      verifiedActionCount: serverVerifiedCount,
      delta: next.delta,
    });
  };

  const handleToggleAction = async () => {
    if (!currentUserId) {
      onAuthRequired?.();
      return;
    }
    if (!canLogPostAction(post)) return;

    setSubmitting(true);
    try {
      if (hasLogged) {
        const result = await removePostAction(post.id);
        applyToggle(
          false,
          result?.action_count,
          result?.verified_action_count,
          -1
        );
      } else {
        const result = await logPostAction(post.id, {
          actionType: post.action_type,
          billId: post.bill?.id,
        });
        applyToggle(
          true,
          result?.action_count,
          result?.verified_action_count,
          1
        );
      }
    } catch (error) {
      const code = resolveToggleErrorCode(error);
      const fallback = getToggleFallbackFromErrorCode(code);
      if (fallback) {
        applyToggle(
          fallback.logged,
          fallback.serverCount,
          fallback.serverVerifiedCount,
          fallback.delta
        );
      } else {
        console.error("Error toggling post action:", error);
        onError?.(error.response?.data?.error || "Couldn't update action. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const actionSummaryLabel = getActionSummaryLabel(displayActionCount, hasLogged);
  const showToggleButton = shouldShowActionToggle({ post, isAuthor });
  const showRow = shouldShowActionRow({ post, isAuthor, displayActionCount });

  const openActionsModal = () => {
    if (!currentUserId) {
      onAuthRequired?.();
      return;
    }
    setModalOpen(true);
    loadActions();
  };

  if (!showRow) return null;

  return (
    <>
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: displayActionCount > 0 ? "space-between" : "flex-start",
          flexWrap: "wrap",
          gap: 1,
          mb: 0,
        }}
      >
        {displayActionCount > 0 ? (
          <Button
            variant="text"
            disableRipple
            onClick={openActionsModal}
            aria-label={getActionSummaryAccessibilityLabel(displayActionCount, hasLogged)}
            sx={{
              flex: "0 1 auto",
              minWidth: 0,
              maxWidth: "100%",
              width: "auto",
              alignSelf: "center",
              justifyContent: "flex-start",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              color: "#0f172a",
              textDecoration: "underline",
              px: 0,
              py: 0.5,
              minHeight: 36,
              bgcolor: "transparent",
              "&:hover": {
                textDecoration: "underline",
                bgcolor: "transparent",
                color: "#0f172a",
              },
              "&:active": {
                bgcolor: "transparent",
              },
              "&.Mui-focusVisible": {
                bgcolor: "transparent",
                outline: `2px solid ${theme.primary}`,
                outlineOffset: 2,
              },
            }}
          >
            {actionSummaryLabel}
          </Button>
        ) : null}

        {showToggleButton ? (
          <Button
            variant="outlined"
            onClick={handleToggleAction}
            disabled={submitting}
            aria-label={hasLogged ? "Undo your logged action" : "Log that you took this action"}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12,
              lineHeight: 1.2,
              borderRadius: "999px",
              borderColor: theme.primary,
              color: theme.primary,
              bgcolor: hasLogged ? `${theme.primary}12` : "#ffffff",
              px: 1.5,
              py: 0.25,
              minHeight: 28,
              flexShrink: 0,
              "&:hover": {
                borderColor: theme.primary,
                bgcolor: hasLogged ? `${theme.primary}18` : `${theme.primary}08`,
              },
            }}
          >
            {submitting ? (
              <CircularProgress size={14} sx={{ color: theme.primary }} />
            ) : hasLogged ? (
              "Undo"
            ) : (
              "Action taken"
            )}
          </Button>
        ) : null}
      </Box>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullWidth
        maxWidth="xs"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
            fontSize: 18,
            fontWeight: 600,
            color: "#0f172a",
          }}
        >
          {displayActionCount} {displayActionCount === 1 ? "person" : "people"} took this action
          <IconButton aria-label="Close" onClick={() => setModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} sx={{ color: theme.primary }} />
            </Box>
          ) : actions.length === 0 ? (
            <Typography sx={{ textAlign: "center", color: "#64748b", py: 4, px: 2 }}>
              No actions logged yet.
            </Typography>
          ) : (
            <List disablePadding>
              {actions.map((item) => (
                <ListItem key={item.id} sx={{ px: 2, py: 1.25 }}>
                  <ListItemAvatar>
                    <Avatar src={item.user?.profile_image} sx={{ width: 36, height: 36, bgcolor: theme.primary }}>
                      {getActionUserInitials(item.user)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={getActionUserDisplayName(item.user)}
                    secondary={item.action_type ? formatActionTypeLabel(item.action_type) : null}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: 14, color: "#0f172a" }}
                    secondaryTypographyProps={{ fontSize: 12, color: "#64748b" }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
